import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useBranch, SINGLE_BRANCH_MODE } from "@/context/BranchContext";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Building2, LayoutGrid, LogOut, ChevronsUpDown, Check } from "lucide-react";

export default function ProfileMenu() {
  const { user, logout } = useAuth();
  const { branches, selectedBranchId, setBranch, currentBranch } = useBranch();
  const navigate = useNavigate();

  const initials = (user?.name || user?.username || "?").charAt(0).toUpperCase();
  const showBranchSwitcher = !SINGLE_BRANCH_MODE && branches.length > 1;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className="flex items-center gap-2 rounded-full pl-1 pr-3 py-1 hover:bg-sand transition-colors outline-none"
        data-testid="profile-menu-trigger"
      >
        <Avatar className="h-8 w-8">
          <AvatarFallback className="bg-forest text-white text-xs font-bold">{initials}</AvatarFallback>
        </Avatar>
        <div className="hidden sm:block text-left leading-tight">
          <p className="text-sm font-medium text-forest truncate max-w-[140px]">{user?.name || user?.username}</p>
          <p className="text-[11px] text-sage truncate max-w-[140px]">
            {currentBranch ? currentBranch.name : "No branch selected"}
          </p>
        </div>
        <ChevronsUpDown className="h-4 w-4 text-sage" />
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-60" data-testid="profile-menu-content">
        <DropdownMenuLabel>
          <div className="flex flex-col">
            <span className="text-forest">{user?.name || user?.username}</span>
            <span className="text-xs font-normal text-sage capitalize">{user?.role}</span>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />

        {showBranchSwitcher && (
          <DropdownMenuSub>
            <DropdownMenuSubTrigger data-testid="branch-switcher-trigger">
              <Building2 className="h-4 w-4 mr-2 text-sage" />
              <span className="flex-1">Branch</span>
              <span className="text-xs text-sage mr-1">{currentBranch?.name || "Select"}</span>
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent>
              <DropdownMenuRadioGroup value={selectedBranchId || ""} onValueChange={setBranch}>
                {branches.map((b) => (
                  <DropdownMenuRadioItem
                    key={b.id}
                    value={b.id}
                    data-testid={`branch-option-${b.id}`}
                  >
                    {b.id === selectedBranchId && <Check className="h-3.5 w-3.5 mr-1 text-forest" />}
                    {b.name}
                  </DropdownMenuRadioItem>
                ))}
              </DropdownMenuRadioGroup>
            </DropdownMenuSubContent>
          </DropdownMenuSub>
        )}

        {user?.role === "admin" && (
          <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="admin-console-link">
            <LayoutGrid className="h-4 w-4 mr-2 text-sage" />
            Admin console
          </DropdownMenuItem>
        )}

        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={logout}
          className="text-destructive focus:text-destructive"
          data-testid="profile-signout"
        >
          <LogOut className="h-4 w-4 mr-2" />
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
